import { Avatar, Box, Button, Grid, IconButton, TextField, Typography, Dialog, DialogContent, DialogTitle, InputAdornment, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react'
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import ApartmentIcon from '@mui/icons-material/Apartment';
import UpdateIcon from '@mui/icons-material/Update';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import { ApiGetUserBankDetails, ApiGetUserDetails, ApiUpdateUser } from '../../apiService/Core/Apicall';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PopupDialog from '../../Components/Popups/ConfirmDialog';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  countryCode?: string;
  state?: string | null;
  address?: string | null;
  country?: string;
  profile_pic?: string;
  lastLoggedin?: string;
  isCryptoOnboardCompleted?: string;
  crypto_status?: string;
  id_type?: string;
  id_number?: string
  bankAccounts?: Array<{
    bankAccountId: number;
    bankName: string;
    accountLast4: string;
    name: string;
    isSelected: boolean;
    accountType: string;
    status: string;
    createdDate: string;
    connectionId: string;
  }>;
}

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.id;
  const aeropay_userId = location.state?.aeropay_userId;
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userData, setuserData] = useState<User | null>(null);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState<any>(null);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  })
  const [successPopup, setSuccessPopup] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  })
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validate = () => {
    let tempErrors = { firstName: "", lastName: "", email: "", password: "", confirmPassword: "" };
    let isValid = true;

    if (!form.firstName.trim()) {
      tempErrors.firstName = "First Name is required";
      isValid = false;
    }

    if (!form.lastName.trim()) {
      tempErrors.lastName = "Last Name is required";
      isValid = false;
    }

    if (!form.email.trim()) {
      tempErrors.email = "Email is required";
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        tempErrors.email = "Invalid email format";
        isValid = false;
      }
    }

    if (form.password) {
      if (form.password !== form.confirmPassword) {
        tempErrors.confirmPassword = "Passwords do not match";
        isValid = false;
      }
    }

    setErrors(tempErrors);
    return isValid;
  };

  const fetchUserDetails = useCallback(async () => {
    try {
      const res = await ApiGetUserDetails(`/${userId}`);
      if (res?.status) {
        setuserData(res.data);
        setForm({
          firstName: res?.data?.firstName || "",
          lastName: res?.data?.lastName || "",
          email: res?.data?.email || "",
          password: "",
          confirmPassword: "",
        });
        try {
          const bankRes = await ApiGetUserBankDetails(`?userId=${aeropay_userId}`);
          if (bankRes?.status && bankRes?.data?.bankAccounts) {
            setuserData((prevData: any) => ({
              ...prevData,
              bankAccounts: bankRes.data.bankAccounts
            }));
          }
        } catch (bankError: any) {
          console.error("Error fetching bank details:", bankError);
        }
      }
    } catch (error: any) {
      console.error("Error Generating User Details:", error);
      setErrorPopup({
        open: true,
        message: error?.message || error?.response?.data?.message || error || 'Something went wrong. Please try again.',
      })
    }
  }, [userId, aeropay_userId]);

  useEffect(() => {
    if (!userId) return;
    fetchUserDetails();
  }, [userId, fetchUserDetails]);

  const handleUpdateUser = async () => {
    if (!validate()) return;

    const payload: any = {
      id: userId,
    };

    if (form.firstName.trim() && form.firstName !== userData?.firstName) {
      payload.firstName = form.firstName;
    }

    if (form.lastName.trim() && form.lastName !== userData?.lastName) {
      payload.lastName = form.lastName;
    }

    if (form.email.trim() && form.email !== userData?.email) {
      payload.email = form.email;
    }

    if (form.password) {
      payload.password = form.password;
      payload.confirmpassword = form.confirmPassword;
    }

    if (Object.keys(payload).length === 1) {
      toast.info("No changes detected");
      return;
    }

    try {
      const res = await ApiUpdateUser(payload);

      if (res?.status) {
        setSuccessPopup({
          open: true,
          message: res?.message || 'User updated successfully.',
        })
        setOpen(false);
        setForm({
          ...form,
          firstName: "",
          lastName: "",
          password: "",
          confirmPassword: ""
        });
        setErrors({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          confirmPassword: ""
        });
        fetchUserDetails();
      }
    } catch (error: any) {
      console.error("Error Updating User.", error)
      setErrorPopup({
        open: true,
        message: error?.message || error?.response?.data?.message || error || 'Something went wrong. Please try again.',
      })
    }
  };

  const closeBankDialog = () => {
    setBankDialogOpen(false);
    setSelectedBankAccount(null);
  };

  const handleViewBankDetails = (account: any) => {
    setSelectedBankAccount(account);
    setBankDialogOpen(true);
  };


  return (
    <div className='layout flex-column'>
      <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
        <Box className="d-flex gap-3 align-items-center">
          <IconButton sx={{ ':hover': { backgroundColor: '#ffffff1A' } }} onClick={() => navigate('/admin/users')}>
            <ArrowBackIcon sx={{ color: "#ffffff" }} />
          </IconButton>
          <Box className="p-2 bg-light-white border-10">
            <PersonIcon sx={{ color: "#ffffff" }} />
          </Box>
          <Typography className='fs-24 text-white fw-medium'>User Details</Typography>
        </Box>
      </Box>
      <Grid container className="w-100 p-2" spacing={2}>
        <Grid className="bg-dark-color d-flex align-items-center justify-content-between gap-2 border-10 w-100 p-4">
          <Box className="d-flex align-items-center gap-4">
            <Avatar
              className="bg-light-white fs-28"
              sx={{ width: 100, height: 100, color: "#f2674a", fontWeight: 700, }}
            >
              {userData
                ? `${userData.firstName?.[0] || ""}${userData.lastName?.[0] || ""}`
                : ""}
            </Avatar>
            <Typography className='fs-36' fontWeight={600} color="#9a9a9a">
              {userData ? `${userData.firstName} ${userData.lastName}` : "—"}
            </Typography>
          </Box>
          <Box className="d-flex gap-2 align-items-center">
            <Tooltip title="Update User Details" arrow>
              <Button variant="contained" className='bg-orange' onClick={() => setOpen(true)}>
                Update User
              </Button>
            </Tooltip>
          </Box>
        </Grid>
        <Grid className="bg-dark-color d-flex flex-column border-10" size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
          <Box
            sx={{
              backgroundColor: "#0e0e0e",
              borderRadius: "14px",
              overflow: "hidden",
              transition: "all .35s ease",
              "&:hover": {
                transform: "scale(1.015)",
                boxShadow: "0 0 40px rgba(255,106,61,.35)",
                "& .section-icon": {
                  transform: "rotate(12deg) scale(1.1)",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 3,
                py: 2.5,
                background: "linear-gradient(90deg, #2b120c, #120907)",
              }}
            >
              <Box
                className="section-icon"
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  backgroundColor: "#3a1811",
                  transition: "all .35s ease",
                }}
              >
                <EmailIcon sx={{ color: "#ff6a3d" }} />
              </Box>
              <Typography fontSize={20} fontWeight={600} color="#9a9a9a">
                Contact Information
              </Typography>
            </Box>
            <Grid container className="p-4" columnSpacing={6}>
              <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                <Info
                  label="Email Address"
                  value={userData?.email}
                  icon={<EmailIcon />}
                  color="#ff6a3d"
                />
              </Grid>

              <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                <Info
                  label="Phone Number"
                  value={`${userData?.phoneNumber}`}
                  icon={<PhoneIcon />}
                  color="#60a5fa"
                />
              </Grid>

              <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                <Info
                  label="User ID"
                  value={userData?._id}
                  icon={<PersonIcon />}
                  color="#c084fc"
                />
              </Grid>
            </Grid>

          </Box>
        </Grid>
        <Grid className="bg-dark-color d-flex flex-column border-10" size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
          <Box
            sx={{
              backgroundColor: "#0e0e0e",
              borderRadius: "14px",
              overflow: "hidden",
              transition: "all .35s ease",
              "&:hover": {
                transform: "scale(1.015)",
                boxShadow: "0 0 40px rgba(74,222,128,.35)",
                "& .section-icon": {
                  transform: "rotate(12deg) scale(1.1)",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 3,
                py: 2.5,
                background: "linear-gradient(90deg, #0f1f14, #0a120d)",
              }}
            >
              <Box
                className="section-icon"
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  backgroundColor: "#163322",
                  transition: "all .35s ease",
                }}
              >
                <LocationOnIcon sx={{ color: "#4ade80" }} />
              </Box>
              <Typography fontSize={20} fontWeight={600} color="#9a9a9a">
                Location Details
              </Typography>
            </Box>
            <Grid container className="p-4" sx={{ gap: 6 }} columnSpacing={6}>
              <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                <Info label="Primary Address" value={userData?.address || "-"} icon={<HomeIcon />} color="#4ade80" />
              </Grid>
              <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                <Info label="Country Code" value={userData?.countryCode || "-"} icon={<LocationOnIcon />} color="#22d3ee" />
              </Grid>

              <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                <Info label="State" value={userData?.state || "-"} icon={<LocationOnIcon />} color="#22d3ee" />
              </Grid>

              <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                <Info label="Country" value={userData?.country || "-"} icon={<LocationOnIcon />} color="#38bdf8" />
              </Grid>
            </Grid>

          </Box>
        </Grid>
        <Grid className="bg-dark-color d-flex flex-column border-10" size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
          <Box
            sx={{
              backgroundColor: "#0e0e0e",
              borderRadius: "14px",
              overflow: "hidden",
              transition: "all .35s ease",
              "&:hover": {
                transform: "scale(1.015)",
                boxShadow: "0 0 40px rgba(245,158,11,.35)",
                "& .section-icon": {
                  transform: "rotate(12deg) scale(1.1)",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 3,
                py: 2.5,
                background: "linear-gradient(90deg, #2a1f0b, #141007)",
              }}
            >
              <Box
                className="section-icon"
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  backgroundColor: "#3b2a10",
                  transition: "all .35s ease",
                }}
              >
                <ApartmentIcon sx={{ color: "#f59e0b" }} />
              </Box>
              <Typography fontSize={20} fontWeight={600} color="#9a9a9a">
                Additional Information
              </Typography>
            </Box>
            <Grid container className="p-4" sx={{ gap: 6 }} columnSpacing={6}>
              <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                <Info
                  label="sFox Status"
                  value={userData?.crypto_status === "PENDING" ? "Ongoing" : userData?.crypto_status === "COMPLETED" ? "Completed" : "-"}
                  icon={<ApartmentIcon />}
                  color="#f59e0b"
                />
              </Grid>
              <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                <Info
                  label="sFox Registered"
                  value={userData?.isCryptoOnboardCompleted === "true" ? "Yes" : "No"}
                  icon={<AppRegistrationIcon />}
                  color="#f59e0b"
                />
              </Grid>
              <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                <Info
                  label="sFox Document Type"
                  value={userData?.id_type || "-"}
                  icon={<AssignmentIcon />}
                  color="#ec4899"
                />
              </Grid>

              <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                <Info
                  label="sFox Document Id Number"
                  value={userData?.id_number || "-"}
                  icon={<UpdateIcon />}
                  color="#60a5fa"
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid className="bg-dark-color d-flex flex-column border-10" size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
          <Box
            sx={{
              backgroundColor: "#0e0e0e",
              borderRadius: "14px",
              overflow: "hidden",
              transition: "all .35s ease",
              "&:hover": {
                transform: "scale(1.015)",
                boxShadow: "0 0 40px rgba(59,130,246,.35)",
                "& .section-icon": {
                  transform: "rotate(12deg) scale(1.1)",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 3,
                py: 2.5,
                background: "linear-gradient(90deg, #0f172a, #010819)",
              }}
            >
              <Box
                className="section-icon"
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  backgroundColor: "#1e3a5f",
                  transition: "all .35s ease",
                }}
              >
                <AccountBalanceIcon sx={{ color: "#3b82f6" }} />
              </Box>
              <Typography fontSize={20} fontWeight={600} color="#9a9a9a">
                Linked Bank Accounts
              </Typography>
            </Box>
            <Grid container className="p-4" columnSpacing={3} rowSpacing={2}>
              {userData?.bankAccounts && userData.bankAccounts.length > 0 ? (
                userData?.bankAccounts?.map((account: any) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }} key={account.bankAccountId}>
                    <Box
                      sx={{
                        backgroundColor: "#1a1a1a",
                        border: account.isSelected === true ? "1px solid #10b981" : "1px solid #3f3f3f",
                        borderRadius: "10px",
                        p: 2.5,
                        position: "relative",
                        transition: "all .35s ease",
                        "&:hover": {
                          borderColor: "#3b82f6",
                          boxShadow: "0 0 20px rgba(59,130,246,.25)",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: "8px",
                            backgroundColor: "#1e3a5f",
                          }}
                        >
                          <AccountBalanceIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography fontSize={13} color="#6f6f6f" textTransform="uppercase">
                            {account.bankName}
                          </Typography>
                          <Typography fontSize={14} fontWeight={600} color="#d1d1d1">
                            {account.accountType}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 1.5, gap: 2, display: "flex", alignItems: "center" }}>
                          <Box>
                            {account.isSelected === true && (
                              <Tooltip title="Primary Account" arrow>
                                <DoneAllIcon sx={{ color: "var(--primary-bg-color)" }} />
                              </Tooltip>
                            )}
                          </Box>
                          <Box sx={{ display: "inline-block", backgroundColor: account.status === "verified" ? "#10b98150" : "#f59e0b50", border: account.status === "verified" ? "1px solid #10b981" : "1px solid #f59e0b", color: account.status === "verified" ? "#10b981" : "#f59e0b", padding: "4px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, textTransform: "capitalize" }}>
                            {account.status}
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ py: 1.5, borderTop: "1px solid #3f3f3f", borderBottom: "1px solid #3f3f3f" }}>
                        <Typography fontSize={11} color="#6f6f6f" textTransform="uppercase" sx={{ mb: 0.5 }}>
                          Account Number
                        </Typography>
                        <Typography fontSize={13} color="#d1d1d1" sx={{ fontFamily: "monospace", letterSpacing: "2px" }}>
                          ****{account.accountLast4}
                        </Typography>
                      </Box>
                      <Box sx={{ pt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography fontSize={11} color="#6f6f6f">
                          {account.name}
                        </Typography>
                        <Tooltip title="View Bank Details" arrow>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              backgroundColor: "#3b82f6",
                              fontSize: "11px",
                              padding: "4px 12px",
                              "&:hover": {
                                backgroundColor: "#2563eb",
                              },
                            }}
                            onClick={() => handleViewBankDetails(account)}
                          >
                            View
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>
                ))
              ) : (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      py: 6,
                      border: "1px dashed #3f3f3f",
                      borderRadius: "10px",
                      backgroundColor: "#1a1a1a",
                    }}
                  >
                    <AccountBalanceIcon sx={{ fontSize: 40, color: "#6f6f6f", mb: 1 }} />
                    <Typography color="#6f6f6f" fontSize={14}>
                      No Bank Account Found
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#2a2a2a",
            borderRadius: "14px",
            boxShadow: "0 0 40px rgba(255,106,61,.35)"
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f2674a",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            marginBottom: 2
          }}
        >
          Update User
          <IconButton onClick={() => {
            setOpen(false);
            setForm({
              ...form,
              firstName: "",
              lastName: "",
              password: "",
              confirmPassword: ""
            });
            setErrors({
              firstName: "",
              lastName: "",
              email: "",
              password: "",
              confirmPassword: ""
            });
          }}>
            <CloseIcon sx={{ color: "#fff" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#2a2a2a" }}>
          <Box className="d-flex flex-column align-items-center gap-3 p-2">
            <TextField
              sx={inputStyle}
              fullWidth
              label="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
            <TextField
              sx={inputStyle}
              fullWidth
              label="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
            <TextField
              sx={inputStyle}
              fullWidth
              label="Email Id"
              value={form.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              sx={inputStyle}
              fullWidth
              label="New Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ color: "#9ca3af" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              sx={inputStyle}
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={form.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      sx={{ color: "#9ca3af" }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        </DialogContent>
        <Box
          sx={{
            height: "2px",
            backgroundColor: "#3f3f3f",
            my: 1,
          }}
        />
        <Box className="d-flex justify-content-end gap-2 p-2 align-items-center">
          <Button
            variant="outlined"
            onClick={() => {
              setOpen(false);
              setForm({
                ...form,
                firstName: "",
                lastName: "",
                password: "",
                confirmPassword: ""
              });
              setErrors({
                firstName: "",
                lastName: "",
                email: "",
                password: "",
                confirmPassword: ""
              });
            }}
            sx={{
              borderColor: "#9ca3af",
              color: "#9ca3af",
              "&:hover": {
                borderColor: "#fff",
                color: "#fff",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            className="bg-orange"
            onClick={handleUpdateUser}
          >
            Update
          </Button>
        </Box>
      </Dialog>
      <Dialog
        open={bankDialogOpen}
        onClose={closeBankDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#2a2a2a",
            borderRadius: "14px",
            boxShadow: "0 0 40px rgba(59,130,246,.35)"
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#3b82f6",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            marginBottom: 2
          }}
        >
          Bank Account Details
          <IconButton onClick={closeBankDialog}>
            <CloseIcon sx={{ color: "#fff" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#2a2a2a" }}>
          <Box className="d-flex flex-column gap-4 p-2">
            <BankInfoField
              label="Account Name"
              value={selectedBankAccount?.name || "-"}
              icon={<PersonIcon />}
              color="#ec4899"
            />
            <BankInfoField
              label="Account Number"
              value={selectedBankAccount?.accountLast4 ? `****${selectedBankAccount.accountLast4}` : "-"}
              icon={<AssignmentIcon />}
              color="#f59e0b"
            />
            <BankInfoField
              label="Bank Name"
              value={selectedBankAccount?.bankName || "-"}
              icon={<AccountBalanceIcon />}
              color="#3b82f6"
            />
            <BankInfoField
              label="Account Type"
              value={selectedBankAccount?.accountType || "-"}
              icon={<UpdateIcon />}
              color="#d885fc"
            />
          </Box>
        </DialogContent>
        <Box
          sx={{
            height: "2px",
            backgroundColor: "#3f3f3f",
            my: 1,
          }}
        />
        <Box className="d-flex justify-content-end gap-2 p-2 align-items-center">
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#3b82f6",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
            onClick={closeBankDialog}
          >
            Close
          </Button>
        </Box>
      </Dialog>
      <PopupDialog
        open={errorPopup.open}
        onClose={() => setErrorPopup({ open: false, message: '' })}
        onConfirm={() => setErrorPopup({ open: false, message: '' })}
        variant="error"
        title="Something Went Wrong"
        message={errorPopup.message}
        confirmText="Try Again"
        cancelText="Dismiss"
      />
      <PopupDialog
        open={successPopup.open}
        onClose={() => setSuccessPopup({ open: false, message: '' })}
        onConfirm={() => setSuccessPopup({ open: false, message: '' })}
        variant="success"
        title="Success"
        message={successPopup.message}
        confirmText="Done"
        cancelText="Close"
      />
    </div>
  )
}
export default UserDetails;

const Info = ({ label, value, icon, color }: any) => (
  <Box sx={{ display: "flex", gap: 1.5 }}>
    {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
    <Box>
      <Typography fontSize={11} color="#6f6f6f" textTransform="uppercase">
        {label}
      </Typography>
      <Typography fontSize={14} color="#d1d1d1">
        {value}
      </Typography>
    </Box>
  </Box>
);

const BankInfoField = ({ label, value, icon, color }: any) => (
  <Box sx={{ display: "flex", gap: 2, py: 1.5, borderBottom: "1px solid #3f3f3f" }}>
    {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
    <Box sx={{ flex: 1 }}>
      <Typography fontSize={12} color="#6f6f6f" textTransform="uppercase" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography fontSize={15} color="#d1d1d1" sx={{ wordBreak: "break-all" }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const inputStyle = {
  input: { color: "#fff" },

  label: { color: "#fff" },

  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#fff",
    },
    "&:hover fieldset": {
      borderColor: "#cc583f",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#f2674a",
    },
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#f2674a",
  },
};
